import { computed, inject, Type } from '@angular/core';
import {
  patchState,
  signalStore,
  withComputed,
  withMethods,
  withProps,
  withState,
} from '@ngrx/signals';

import { OrganizationsV2Service } from '../../services';
import {
  ORGANIZATION_DETAIL_DEFAULT_SIGNAL_STORE,
  createOrganizationDetailDefaultSignalStore,
  setOrganizationPageError,
  setOrgLoadingState,
} from '../../constants';
import { rxMethod } from '@ngrx/signals/rxjs-interop';
import { finalize, Observable, pipe, switchMap, tap, throwError } from 'rxjs';
import { tapResponse } from '@ngrx/operators';
import {
  CreateOrganizationAdminPayload,
  OrganizationAdminResult,
  OrganizationDetailSignalStore,
  OrganizationMembersLoadingType,
} from '@data-access/organizations';
import { OrganizationAddressesResponseBody } from '@erm/cluster';

export const ORGANIZATION_DETAIL_SIGNAL_STORE = signalStore(
  withState(() => inject(ORGANIZATION_DETAIL_DEFAULT_SIGNAL_STORE)),
  withProps((store) => ({
    _organizationV2Service: inject(OrganizationsV2Service),
    store,
  })),
  withComputed(({ ...store }) => ({
    $error: computed(() => store.error()),
    $loading: computed(() => store.loading()),
    $organizationKey: computed(() => store.organizationKey()),
    $organization: computed(() => {
      const _organization = store.organization();
      if (!_organization) {
        return null;
      }
      return _organization;
    }),
    $organizationAddresses: computed(() => {
      const _organizationAddresses = store.organizationAddresses();
      if (!_organizationAddresses?.data?.results?.length) {
        return [];
      }
      const _sortedResults: OrganizationAddressesResponseBody[] =
        _organizationAddresses?.data.results.filter((o) => !!o.is_primary);
      _organizationAddresses.data.results = [..._sortedResults];
      return _organizationAddresses;
    }),
    $organizationMembers: computed(() => store.organizationMembers()),
    $membersError: computed(() => store.membersError()),
    $membersLoadingTypes: computed(() => Array.from(store.membersLoading())),
    $isFetchingMembers: computed(() =>
      store.membersLoading().has('fetching-members'),
    ),
    $isAddingMember: computed(() =>
      store.membersLoading().has('adding-member'),
    ),
    $isRemovingMember: computed(() =>
      store.membersLoading().has('removing-member'),
    ),
    $isMutatingMembers: computed(
      () =>
        store.membersLoading().has('adding-member') ||
        store.membersLoading().has('removing-member'),
    ),
  })),
  withMethods(({ _organizationV2Service, ...store }) => {
    const _addMembersLoading = (type: OrganizationMembersLoadingType) => {
      const nextLoading = new Set(store.$membersLoadingTypes());
      nextLoading.add(type);
      patchState(store, { membersLoading: nextLoading });
    };

    const _removeMembersLoading = (type: OrganizationMembersLoadingType) => {
      const nextLoading = new Set(store.$membersLoadingTypes());
      nextLoading.delete(type);
      patchState(store, { membersLoading: nextLoading });
    };

    /*
     * @load Organization
     * */
    const _fetchOrganization = rxMethod<{ organization_key: string }>(
      pipe(
        tap(() =>
          patchState(store, {
            loading: setOrgLoadingState('Organization', true),
            error: null,
          }),
        ),
        switchMap(({ organization_key }) =>
          _organizationV2Service.fetchOrg(organization_key).pipe(
            tapResponse({
              error: (error: Error) =>
                patchState(store, {
                  loading: setOrgLoadingState('Organization', false),
                  error: setOrganizationPageError('Organization', error),
                }),
              next: (organization) =>
                patchState(store, {
                  organization: { ...organization },
                  error: null,
                  loading: setOrgLoadingState('Organization', false),
                }),
            }),
          ),
        ),
      ),
    );

    const _fetchOrganizationAddresses = rxMethod<{
      organization_key: string;
    }>(
      pipe(
        tap(() =>
          patchState(store, {
            loading: setOrgLoadingState('OrganizationAddresses', true),
            error: null,
          }),
        ),
        switchMap(({ organization_key }) =>
          _organizationV2Service.fetchOrgAddresses(organization_key).pipe(
            tapResponse({
              error: (error: Error) =>
                patchState(store, {
                  loading: setOrgLoadingState('OrganizationAddresses', false),
                  error: setOrganizationPageError(
                    'OrganizationAddresses',
                    error,
                  ),
                }),
              next: (organizationAddresses) =>
                patchState(store, {
                  organizationAddresses: organizationAddresses,
                  error: null,
                  loading: setOrgLoadingState('OrganizationAddresses', false),
                }),
            }),
          ),
        ),
      ),
    );

    const _fetchOrganizationMembers = rxMethod<string>(
      pipe(
        tap(() => {
          patchState(store, {
            membersError: null,
          });
          _addMembersLoading('fetching-members');
        }),
        switchMap((organizationKey) =>
          _organizationV2Service.fetchOrganizationAdmins(organizationKey).pipe(
            tapResponse({
              next: (members) =>
                patchState(store, {
                  organizationMembers: members ?? [],
                  membersError: null,
                }),
              error: (error: Error) =>
                patchState(store, {
                  organizationMembers: [],
                  membersError: error,
                }),
            }),
            finalize(() => _removeMembersLoading('fetching-members')),
          ),
        ),
      ),
    );

    return {
      fetchOrganization: (): void => {
        const orgKey = store.$organizationKey();
        if (!orgKey) return;
        _fetchOrganization({ organization_key: orgKey });
      },
      fetchOrganizationAddresses: (): void => {
        const orgKey = store.$organizationKey();
        if (!orgKey) return;
        _fetchOrganizationAddresses({ organization_key: orgKey });
      },
      fetchOrganizationMembers: (): void => {
        const orgKey = store.$organizationKey();
        if (!orgKey) return;
        _fetchOrganizationMembers(orgKey);
      },
      reloadOrganizationMembers: (): void => {
        const orgKey = store.$organizationKey();
        if (!orgKey) return;
        _fetchOrganizationMembers(orgKey);
      },
      addOrganizationMember: (
        payload: CreateOrganizationAdminPayload,
      ): Observable<OrganizationAdminResult> => {
        const orgKey = store.$organizationKey();
        if (!orgKey) {
          return throwError(() => new Error('Organization key is required.'));
        }

        _addMembersLoading('adding-member');
        patchState(store, { membersError: null });

        return _organizationV2Service
          .addOrganizationAdmin(orgKey, payload)
          .pipe(
            tap({
              next: () => {
                _fetchOrganizationMembers(orgKey);
              },
              error: (error: unknown) => {
                if (error instanceof Error) {
                  patchState(store, { membersError: error });
                }
              },
            }),
            finalize(() => _removeMembersLoading('adding-member')),
          );
      },
      removeOrganizationMember: (memberId: string): Observable<void> => {
        const orgKey = store.$organizationKey();
        if (!orgKey) {
          return throwError(() => new Error('Organization key is required.'));
        }

        _addMembersLoading('removing-member');
        patchState(store, { membersError: null });

        return _organizationV2Service
          .removeOrganizationAdmin(orgKey, memberId)
          .pipe(
            tap({
              next: () => {
                _fetchOrganizationMembers(orgKey);
              },
              error: (error: unknown) => {
                if (error instanceof Error) {
                  patchState(store, { membersError: error });
                }
              },
            }),
            finalize(() => _removeMembersLoading('removing-member')),
          );
      },
      clearOrganizationMembers: (): void => {
        patchState(store, {
          organizationMembers: [],
          membersError: null,
          membersLoading: new Set(),
        });
      },
      setOrganizationKey: (key?: string | null): void => {
        patchState(store, { organizationKey: key });
      },
      resetStore: () =>
        patchState(store, createOrganizationDetailDefaultSignalStore()),
    };
  }),
) satisfies Type<OrganizationDetailSignalStore>;
